// src/pages/Team/FloorSnapshot.jsx
// STATUS: active | UPDATED: 2026-08-29
// LOG:
//   2026-08-29  New. The measurement half of the manager's page.
//
// Four numbers a manager can read between customers.
//
// A TRUE ZERO IS NOT A FAKE ZERO
//
// Team.jsx already learned this once: canSeeProgress used to be derived from
// whether any rows came back, which meant a manager whose team genuinely had no
// progress saw the member view and concluded the product was broken. The same
// trap is worse here, because a dashboard of zeros looks like either a failure
// or a lie.
//
// So there are two different empty states and they say different things. No
// teammates yet is a setup problem and says so. Teammates with nothing recorded
// is a real, useful, uncomfortable answer and shows the zeros.
//
// WHAT IS DELIBERATELY NOT HERE
//
// No trend arrows, no week-over-week, no averages. There is not enough history
// for any of it to mean anything, and a sparkline drawn from four data points
// is decoration pretending to be evidence. When there are months of
// activity_events it will be worth revisiting, and not before.
//
// Waiting on you is the sharp number and it sits first, because it is the only
// one that points at the manager rather than the floor.

import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, HStack, VStack, Text, Icon, Spinner, Alert, AlertIcon, SimpleGrid } from '@chakra-ui/react'
import { Users, TrendingUp, Sparkles } from 'lucide-react'
import { loadFloorMetrics, resolveStepNumbers, stepLabel, QUIET_AFTER_DAYS } from '../../lib/floorMetrics'
import { displayNameFor } from '../../lib/names.js'

function Stat({ label, value, note, accent = false }) {
  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 4, md: 5 }}>
      <Text textStyle="eyebrow" color={accent ? 'accent.500' : 'inkMuted'} mb={2}>
        {label}
      </Text>
      <Text fontSize="display-sm" fontWeight={600} color="ink" lineHeight={1.1} letterSpacing="-0.02em">
        {value}
      </Text>
      {note && (
        <Text fontSize="body-sm" color="inkMuted" mt={1.5} lineHeight={1.5}>
          {note}
        </Text>
      )}
    </Box>
  )
}

export default function FloorSnapshot({ members }) {
  const [state, setState] = useState({ loading: true, data: null, error: null })
  const [stepMap, setStepMap] = useState({})

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }))
    const [data, map] = await Promise.all([loadFloorMetrics(members), resolveStepNumbers()])
    setStepMap(map)
    setState({ loading: false, data: data.ok ? data : null, error: data.ok ? null : data.error })
  }, [members])

  useEffect(() => {
    load()
  }, [load])

  if (state.loading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner size="md" color="ink" thickness="3px" />
      </Flex>
    )
  }

  if (state.error) {
    return (
      <Alert status="error" borderRadius="md" fontSize="sm">
        <AlertIcon />
        {state.error}
      </Alert>
    )
  }

  const d = state.data

  // One person on the roster is the platform admin looking at themselves. That
  // is not a floor and pretending to measure it would be the fake data this
  // repo bans.
  if (!d || d.empty || d.people < 2) {
    return (
      <HStack bg="surface" borderRadius="card" px={5} py={4} spacing={3} align="flex-start">
        <Box color="inkMuted" flexShrink={0} pt={0.5}>
          <Users size={17} />
        </Box>
        <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6}>
          The floor numbers appear once there is a floor. Invite your team and this shows what is
          signed off, where people stall and who has gone quiet.
        </Text>
      </HStack>
    )
  }

  const stall = d.stallStepId ? stepLabel(stepMap, d.stallStepId) : null
  const quietNames = d.quiet.slice(0, 3).map((m) => displayNameFor(m, { email: m.email }))

  return (
    <VStack align="stretch" spacing={3}>
      <HStack spacing={2}>
        <Icon as={TrendingUp} boxSize={4} color="inkMuted" />
        <Text textStyle="eyebrow" color="inkMuted">
          The floor, last {QUIET_AFTER_DAYS} days
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Stat
          label="Waiting on you"
          value={d.waiting}
          accent={d.waiting > 0}
          note={
            d.waiting === 0
              ? 'Nothing to sign off'
              : stall && d.stallCount > 1
                ? `Most on ${stall}`
                : 'Passed the check, needs watching'
          }
        />
        <Stat
          label="Signed off"
          value={`${d.signedPct}%`}
          note={`${d.signedOff} of ${d.totalSteps} steps across ${d.people} people`}
        />
        <Stat
          label="Gone quiet"
          value={d.quiet.length}
          accent={d.quiet.length > 0}
          note={
            d.quiet.length === 0
              ? 'Everyone active this week'
              : quietNames.join(', ') + (d.quiet.length > 3 ? ` and ${d.quiet.length - 3} more` : '')
          }
        />
        <Stat
          label="Drills run"
          value={d.drills}
          note={
            d.drills === 0
              ? 'Nobody has practised yet'
              : `${d.practising.length} ${d.practising.length === 1 ? 'person' : 'people'} putting reps in`
          }
        />
      </SimpleGrid>

      {/* Recognition, and only when it was earned. A manager telling somebody
          "the app says you practised" is worth something precisely because the
          app does not say it about everyone. Two reps minimum, and nothing at
          all when the floor has done nothing. */}
      {d.topPractitioner && (
        <HStack bg="surface" borderRadius="card" px={5} py={4} spacing={3} align="flex-start">
          <Box color="accent.500" flexShrink={0} pt={0.5}>
            <Sparkles size={16} />
          </Box>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6}>
            <Text as="span" color="ink" fontWeight={600}>
              {displayNameFor(d.topPractitioner, { email: d.topPractitioner.email })} ran{' '}
              {d.topPractitioner.drills} drills this week.
            </Text>{' '}
            Nobody makes them do that. Worth saying out loud on the floor.
          </Text>
        </HStack>
      )}

      {/* The one line worth reading aloud in a Monday meeting. Only shown when
          more than one person is stuck in the same place, because a single
          person stuck on a step is a conversation, not a pattern. */}
      {stall && d.stallCount > 1 && (
        <HStack bg="surface" borderRadius="card" px={5} py={4} spacing={3} align="flex-start">
          <Box color="accent.500" flexShrink={0} pt={0.5}>
            <TrendingUp size={16} />
          </Box>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6}>
            <Text as="span" color="ink" fontWeight={600}>
              {d.stallCount} people are waiting on {stall}.
            </Text>{' '}
            They have passed the knowledge check and need somebody to watch them run it on the floor.
          </Text>
        </HStack>
      )}
    </VStack>
  )
}
